/**
 * Transient per-creator editor state — open/closed dialog flags,
 * review feedback text, and request-revision mode flags.
 * Never persisted; resets on page reload or campaign switch.
 *
 * Wave 2a (Zustand split). Companion stores: `useCampaignUiStore`
 * (session-only filters + modal flags), `useCampaignDraftsStore`
 * (UID-scoped per-creator drafts).
 */
import { create } from 'zustand';

export type ReviewMode = 'request_revision' | null;

export interface CampaignEditorState {
  scriptSubmitOpen: Record<string, boolean>;
  scriptExpanded: Record<string, boolean>;
  scriptReviewFeedback: Record<string, string>;
  scriptReviewMode: Record<string, ReviewMode>;
  contentSubmitOpen: Record<string, boolean>;
  contentReviewFeedback: Record<string, string>;
  contentReviewUiMode: Record<string, ReviewMode>;
  setScriptSubmitOpen: (creatorId: string, v: boolean) => void;
  setScriptExpanded: (creatorId: string, v: boolean) => void;
  setScriptReviewFeedback: (creatorId: string, s: string) => void;
  setScriptReviewMode: (creatorId: string, mode: ReviewMode) => void;
  setContentSubmitOpen: (creatorId: string, v: boolean) => void;
  setContentReviewFeedback: (creatorId: string, s: string) => void;
  setContentReviewUiMode: (creatorId: string, mode: ReviewMode) => void;
  clearEditor: () => void;
}

const initialEditor: Omit<
  CampaignEditorState,
  | 'setScriptSubmitOpen'
  | 'setScriptExpanded'
  | 'setScriptReviewFeedback'
  | 'setScriptReviewMode'
  | 'setContentSubmitOpen'
  | 'setContentReviewFeedback'
  | 'setContentReviewUiMode'
  | 'clearEditor'
> = {
  scriptSubmitOpen: {},
  scriptExpanded: {},
  scriptReviewFeedback: {},
  scriptReviewMode: {},
  contentSubmitOpen: {},
  contentReviewFeedback: {},
  contentReviewUiMode: {},
};

export const useCampaignEditorStore = create<CampaignEditorState>((set) => ({
  ...initialEditor,
  setScriptSubmitOpen: (id, v) =>
    set((s) => ({ scriptSubmitOpen: { ...s.scriptSubmitOpen, [id]: v } })),
  setScriptExpanded: (id, v) =>
    set((s) => ({ scriptExpanded: { ...s.scriptExpanded, [id]: v } })),
  setScriptReviewFeedback: (id, feedback) =>
    set((s) => ({ scriptReviewFeedback: { ...s.scriptReviewFeedback, [id]: feedback } })),
  setScriptReviewMode: (id, mode) =>
    set((s) => ({ scriptReviewMode: { ...s.scriptReviewMode, [id]: mode } })),
  setContentSubmitOpen: (id, v) =>
    set((s) => ({ contentSubmitOpen: { ...s.contentSubmitOpen, [id]: v } })),
  setContentReviewFeedback: (id, feedback) =>
    set((s) => ({ contentReviewFeedback: { ...s.contentReviewFeedback, [id]: feedback } })),
  setContentReviewUiMode: (id, mode) =>
    set((s) => ({ contentReviewUiMode: { ...s.contentReviewUiMode, [id]: mode } })),
  clearEditor: () => set(initialEditor),
}));
